import { useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { useClientTranslation } from '@/lib/core/i18n';
import { brandifyJncquoi, cn, ImagesToImageMap } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { Icon } from '@/components/ui/icon';
import type { EventListItemDto, FavoriteListDto } from '@/services/platform/api/model';
import { EventFavoriteToggle } from './EventFavoriteToggle';
import { formatCardDateTime } from './format';
import { isMembersOnly } from './visibility';
import { useEventCategoryLabel } from './categories';
import { cardSeatsLeft, isCardSoldOut } from '../rules';

export interface EventCardProps {
  event: EventListItemDto;
  /** Dims the card and disables the favourite (e.g. a members-only event for a non-member). Does NOT block the tap — see `onSelect`. */
  disabled?: boolean;
  /** The user's 'Events' favorite lists — fetched once at the parent screen and shared across cards. */
  favoriteLists: FavoriteListDto[];
  refetchFavoriteLists: () => Promise<void>;
  /** The favorite list already containing this event, if any (from `useEventFavoriteIndex`). */
  favoriteListGuid?: string;
  onRefetchFavoritesNeeded: () => Promise<void>;
  /** Card tap. The screen decides what happens (navigate to details, open the sold-out sheet, or ignore). */
  onSelect?: (event: EventListItemDto) => void;
  className?: string;
}

/**
 * Horizontal event card (`ELP-card-horizontal`): hero image + favourite heart,
 * a members-only / category tag line with venue, title, date-time and the
 * seats-left / sold-out indicator. Sold-out is `isCardSoldOut` (status `Full` or no seats left).
 */
export function EventCard({
  event,
  disabled = false,
  favoriteLists,
  refetchFavoriteLists,
  favoriteListGuid,
  onRefetchFavoritesNeeded,
  onSelect,
  className,
}: EventCardProps) {
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();

  // Fall back to the CMS-configured no-pic image if the event has none or it fails to load;
  // the plain neutral placeholder is the last resort when even that setting is unconfigured.
  // The landscape (2:1) cover when the event has one; else the vertical cover, cropped to fit.
  const coverUrl = event.horizontalImageUrl ?? event.imageUrl;
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [coverUrl]);
  const showImage = !!coverUrl && !imageFailed;
  const fallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  const membersOnly = isMembersOnly(event.visibility);
  const isSoldOut = isCardSoldOut(event);
  const seatsLeft = cardSeatsLeft(event);
  const hasSeats = seatsLeft != null;
  const dateTime = formatCardDateTime(event.startDateTime, locale, event.endDateTime);

  // `disabled` only dims the card and blocks favouriting; the tap still reaches `onSelect`
  // so the screen can decide (e.g. open the sold-out sheet on an otherwise-dimmed card).
  const interactive = !!onSelect;

  return (
    <article className={cn('flex w-full flex-col gap-4 pb-4', disabled && 'opacity-40', className)}>
      <button
        type="button"
        disabled={!interactive}
        onClick={interactive ? () => onSelect?.(event) : undefined}
        className={cn('relative block aspect-2/1 w-full overflow-hidden', interactive && 'cursor-pointer')}
      >
        {showImage ? (
          <img
            src={coverUrl ?? undefined}
            alt={event.title ?? ''}
            className="absolute inset-0 size-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : fallbackImageUrl ? (
          <img
            src={fallbackImageUrl}
            alt=""
            className="absolute inset-0 size-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="bg-jncquoi-neutral-100 absolute inset-0" aria-hidden="true" />
        )}
        {isSoldOut && <span className="absolute inset-0 bg-[#EFEFF499]" aria-hidden="true" />}
        {isSoldOut && (
          <span className="bg-jncquoi-neutral-1000 text-jncquoi-neutral-00 font-pp-neue-montreal absolute right-2 bottom-2 inline-flex items-center justify-center rounded-[2px] px-2 py-1 text-[10px] leading-none font-semibold tracking-[0.5px] uppercase">
            {t('App.Events.FullyBooked')}
          </span>
        )}
      </button>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          {/* tag + location */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {membersOnly && (
                <>
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                    {t('App.Labels.MembersOnly')}
                  </span>
                  {event.eventCategory && (
                    <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
                      ●
                    </span>
                  )}
                </>
              )}
              {event.eventCategory && (
                <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                  {getCategoryLabel(event.eventCategory)}
                </span>
              )}
            </div>
            {event.venueName && (
              <div className="flex shrink-0 items-center gap-1">
                <Icon name="map-pin" width={12} height={12} className="text-jncquoi-neutral-700" />
                <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap">
                  {brandifyJncquoi(event.venueName?.toUpperCase() ?? '')}
                </span>
              </div>
            )}
          </div>

          {/* title + favourite */}
          <div className="flex items-center justify-center gap-4">
            <h3 className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 truncate text-[16px] font-medium tracking-[0.25px]">
              {event.title}
            </h3>
            <EventFavoriteToggle
              size={24}
              event={{ id: event.id, title: event.title, imageUrl: event.imageUrl }}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              favoriteListGuid={favoriteListGuid}
              onRefetchNeeded={onRefetchFavoritesNeeded}
              disabled={disabled}
            />
          </div>
        </div>

        {/* date-time + availability */}
        <div className="flex items-center gap-2">
          {dateTime && (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] whitespace-nowrap">
              {dateTime}
            </span>
          )}
          {hasSeats && dateTime && (
            <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
              ●
            </span>
          )}
          {hasSeats && (
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <Icon name="user-badge-check" width={12} height={12} className="text-[#325d2d]" />
              <span className="font-pp-neue-montreal text-[10px] font-semibold tracking-wide whitespace-nowrap text-[#325d2d] uppercase">
                {t('App.Events.SeatsLeft', { count: seatsLeft ?? 0 })}
              </span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
